import { ArrowCircleDownIcon } from '@heroicons/react/solid';
import { ConfigProvider, Modal, ThemeConfig } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { BiChevronLeft, BiUpload } from 'react-icons/bi';
import { useRecoilValue } from 'recoil';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';

import PDFReaderIframe from '../../IFrame/PDFReaderIframe';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import { notify } from '../../shared/notify';
import BadgeCheckIcon from '../../../../assets/Icons/BadgeCheckIcon';
import DoubleTick from '../../../../assets/Icons/DoubleTick';
import PlayIconCourse from '../../../../assets/Icons/PlayIconCourse';
import TimerClockIcon from '../../../../assets/Icons/TimerClockIcon';
import { localUserID, user } from '../../../atoms/user';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { addToCart, addToRecentlyViewed } from '../../../services/Cart';
import { AddToCartProps } from '../../../utils/interfaces/cart';
import { ISlidCourse } from '../../../utils/interfaces/landingPage';
import { User } from '../../../utils/interfaces/User';

interface SlidBookContainerProps {
  landingPageData: any;
  openModal: (productId: number) => void;

  isScheduleCallModalVisible: {
    value: boolean;
    productId: number;
  };
  setIsScheduleCallModalVisible: React.Dispatch<
    React.SetStateAction<{
      value: boolean;
      productId: number;
    }>
  >;
  slidCourseContainerData: ISlidCourse;
  token?: string;
  openShare?: () => void;
  openPreview?: () => void;
  courseData?: any;
  isPurchased?: boolean;
}

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
    fontFamily: 'inherit',
  },
};
export const extractSlugAndProductId = (slugWithId: string): string => {
  const cleanSlug = slugWithId?.split('?')[0]?.replace(/\/$/, '');
  if (!cleanSlug) return '';

  const productId = cleanSlug.slice(cleanSlug.lastIndexOf('-') + 1);

  const isValidObjectId = /^[a-fA-F0-9]{24}$/.test(productId);

  const urlParams = new URLSearchParams(slugWithId);
  const queryProductId = urlParams.get('productId');

  return isValidObjectId ? productId : (queryProductId as string);
};

const SlidCourseContainer: React.FC<SlidBookContainerProps> = ({
  slidCourseContainerData,
  openShare,
  openPreview,
  courseData,
  openModal,
  landingPageData,
  isPurchased = false,
}) => {
  const twScreen = useTailwindBreakpoint();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [startTime, setStartTime] = useState<Date | null>(null);
  const [divHeight, setDivHeight] = useState('100%');
  const [screenHeight, setScreenHeight] = useState(0);

  const [loadingAddToCart, setLoadingAddToCart] = useState({
    value: false,
    id: 0,
  });

  const userData: User | undefined = useRecoilValue(user);
  const divRef = useRef<HTMLDivElement>(null);
  const screens = useBreakpoint();
  const router = useRouter();
  const localUserId = useRecoilValue(localUserID);

  const handleOk = () => {
    setIsModalVisible(false);
    const currentTime = new Date();

    triggerViewEvent(currentTime);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    const currentTime = new Date();
    if (startTime) {
      triggerSessionEvent(startTime, currentTime);
    }
  };

  const handleAddDataToCart = async (payload: AddToCartProps) => {
    try {
      setLoadingAddToCart({ value: true, id: payload.id });

      await addToCart(payload);
      addToRecentlyViewed(payload.id, userData?.zl_uid);
      notify(
        `${slidCourseContainerData?.bookName} added to cart successfully!`,
        'success',
      );
      router.push('/cart');
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoadingAddToCart({ value: false, id: payload.id });
    }
  };
  useEffect(() => {
    const handleResize = () => {
      if (divRef.current) {
        const screenHeight = window.innerHeight;
        setScreenHeight(window.innerHeight);

        const divHeight = divRef.current.clientHeight;

        if (divHeight > screenHeight) {
          setDivHeight('90vh');
        } else {
          setDivHeight('100%');
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screens]);

  const triggerViewEvent = (start: Date) => {
    setStartTime(start);


    const eventParams: EventDetails = {
      event_name: 'learn_view',
      source: 'books',
      source_type: router.query['cs']
        ? 'comingSoon'
        : router.query['swp']
          ? 'short'
          : 'long',
      source_id: extractSlugAndProductId(router.asPath) as string,
      sub_source: 'sample',
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
  };

  const triggerSessionEvent = (start: Date, end: Date) => {
    const sessionDuration = (end.getTime() - start.getTime()) / 1000;

    const eventParams: EventDetails = {
      event_name: 'learn_session',
      source: 'books',
      source_type: router.query['cs']
        ? 'comingSoon'
        : router.query['swp']
          ? 'short'
          : 'long',
      source_id: extractSlugAndProductId(router.asPath) as string,
      sub_source: 'sample',
      sub_source_id: null,
      unit: 'seconds',
      value: sessionDuration,
    };

    logEvent({
      eventDetails: eventParams,
      userData: userData,
      pathname: router.pathname,
    });
    setStartTime(null);
  };

  return (
    <ConfigProvider theme={config}>
      <div className='relative w-full flex justify-center items-center h-full 2xl:translate-x-0'>
        <div
          ref={divRef}
          style={{
            position: 'relative',
            height: divHeight,
            overflowY: 'auto',
            boxShadow: screens.lg ? '0px 4px 32px 0px #121212' : undefined,
          }}
          className={clsx(
            screenHeight > 800 ? 'p-4' : 'p-4',
            `px-4 md:px-10 lg:px-6 lg:rounded-[16px] z-10 w-full lg:w-fit lg:bg-[#121212] lg:border-[1px] border-[#282829] no-scrollbar lg:overflow-auto min-h-[${divHeight}]`,
          )}
        >
          <RenderIf condition={!twScreen.sm}>
            <div>
              <BiChevronLeft
                onClick={() => router.back()}
                className='w-6 h-6 text-white mb-3'
              />
            </div>
          </RenderIf>
          <div
            onClick={() => {
              slidCourseContainerData?.courseSampleLink &&
                openPreview &&
                openPreview();
            }}
            className='relative flex justify-center w-full items-center'
          >
            <Image
              src={slidCourseContainerData?.bookImage}
              width={308}
              height={154}
              alt='Book Front'
              priority
              className='w-full aspect-[2/1] object-contain rounded-[8px]'
            />

            <ShouldRender check={slidCourseContainerData?.courseSampleLink}>
              <div className='absolute inset-0 flex items-center justify-center'>
                <PlayIconCourse width={screens.xxl ? '70' : '56'} />
              </div>
            </ShouldRender>
          </div>

          <div className='flex flex-col pt-4 lg:pt-2'>
            <ShouldRender check={screens.lg}>
              <div className='py-4 flex flex-col gap-y-2'>
                <ShouldRender
                  check={
                    courseData?.is_available && courseData?.i18n_attributes
                  }
                >
                  <div className='flex items-start justify-between gap-5'>
                    <div className='flex text-white gap-x-1 items-center'>
                      <BadgeCheckIcon />
                      <p className='text-xs'>
                        Premium content with one-time payment.
                      </p>
                    </div>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openShare && openShare();
                      }}
                      className='w-6 h-6 min-w-6 min-h-6 rounded-full flex items-center justify-center bg-grayZl-900 hover:bg-grayZl-800 cursor-pointer'
                    >
                      <BiUpload className='w-[18px] h-[18px] text-white' />
                    </div>
                  </div>
                  <ShouldRender check={!isPurchased}>
                    <div>
                      <span className='flex xxxs:gap-x-1 md:gap-x-2 items-center pl-1'>
                        <p className='text-2xl font-semibold text-white whitespace-nowrap'>
                          {courseData?.currency_symbol}
                          {courseData?.main_price}
                        </p>
                        <p className='line-through text-[#FFFFFF99] text-base font-medium whitespace-nowrap'>
                          {courseData?.currency_symbol}
                          {courseData?.stroked_price}
                        </p>
                        {courseData?.stroked_price && courseData?.main_price ? (
                          <p className='hidden xxxs:flex gap-x-1 pl-1 md:pl-0 text-[#16A34A] font-bold text-base items-center'>
                            {Math.round(
                              ((courseData.stroked_price -
                                Number(courseData.main_price)) /
                                courseData.stroked_price) *
                              100,
                            )}
                            %{' '}
                            <ArrowCircleDownIcon className='w-3 h-3 md:w-5 md:h-5' />
                          </p>
                        ) : null}
                      </span>
                    </div>
                    <div className='flex items-center text-sm text-[#EF4444]'>
                      <TimerClockIcon />
                      <span className='inline-flex px-1 font-bold'>2 days</span>
                      <span className='translate-y-[1px] font-medium'>
                        {' '}
                        left at this price!
                      </span>
                    </div>
                  </ShouldRender>
                </ShouldRender>
              </div>

              <div className='flex gap-y-3 flex-col'>
                <div className='flex flex-col '>
                  {isPurchased ? (
                    <ButtonDefault
                      size={4}
                      rounded={false}
                      roundedSm={true}
                      enableScaling={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push('/library/?tab=3');
                      }}
                      variant={ButtonVariants.PRIMARY}
                      className='flex w-full justify-center z-20 px-5 py-3 font-medium text-xs whitespace-nowrap'
                    >
                      <span className='px-4 text-sm md:text-base font-semibold py-1'>
                        Start Learning
                      </span>
                    </ButtonDefault>
                  ) : (
                    <>
                      <ButtonDefault
                        size={4}
                        rounded={false}
                        roundedSm={true}
                        enableScaling={false}
                        available={
                          courseData?.is_available && courseData?.i18n_attributes
                        }
                        loading={
                          Number(slidCourseContainerData?.productId) ===
                          loadingAddToCart.id && loadingAddToCart.value
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddDataToCart({
                            id: Number(
                              slidCourseContainerData?.productId,
                            ) as number,
                            temp_user_id: localUserId,
                            quantity: 1,
                            trackingData: {
                              productData: {
                                price: Number(slidCourseContainerData?.main_price),
                                id: Number(slidCourseContainerData?.productId),
                                heading: slidCourseContainerData?.bookName as string,
                                quantity: 1 as number,
                                currency: courseData?.i18n_attributes?.currency,
                                category: landingPageData?.form?.tags?.[0],
                              },
                              userData,
                              pathname: router.pathname,
                              productType: 'hardcopy',
                              type: 'courses',
                            },
                          });
                        }}
                        variant={ButtonVariants.PRIMARY}
                        className='flex w-full justify-center  z-20 px-5 py-3 font-medium text-xs whitespace-nowrap'
                      >
                        <span className='px-4 text-sm md:text-base font-semibold py-1'>
                          Buy Now
                        </span>
                      </ButtonDefault>
                      <span className='flex bg-[#340905] z-10 gap-x-1  pt-1 font-medium text-xs text-[#FF7837] justify-center items-center pb-0.5 rounded-b-[8px]'>
                        <DoubleTick /> Lifetime Access
                      </span>
                    </>
                  )}
                </div>
                <ShouldRender check={landingPageData?.form?.scheduleCall && !isPurchased}>
                  <ButtonDefault
                    size={4}
                    rounded={false}
                    roundedSm={true}
                    enableScaling={false}
                    onClick={() => {
                      openModal(Number(slidCourseContainerData?.productId));
                    }}
                    variant={ButtonVariants.WHITE}
                    className='flex w-full justify-center  px-5 py-3  border-none font-medium text-xs whitespace-nowrap'
                  >
                    <span className='px-4 text-sm md:text-base font-semibold py-1'>
                      Talk To Us
                    </span>
                  </ButtonDefault>
                </ShouldRender>
                {/* 
                <ButtonDefault
                  size={4}
                  rounded={false}
                  roundedSm={true}
                  enableScaling={false}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openShare && openShare();
                  }}
                  variant={ButtonVariants.GRAY}
                  className='flex w-full justify-center  px-5 py-3  border-none font-medium text-xs whitespace-nowrap'
                >
                  <span className='px-4 text-sm md:text-base font-semibold py-1'>
                    Share
                  </span>
                </ButtonDefault> */}
              </div>
            </ShouldRender>
          </div>

          <Modal
            open={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            footer={null}
            width='100%'
            className='custom-modal-bg customLoginModal'
            closable={false}
            style={{ top: 0, backgroundColor: 'transparent' }}
            centered={true}
            destroyOnHidden
          >
            <div
              className='no-select custom-modal-bg'
              id='trackingModal'
              style={{ height: '100%' }}
            >
              <PDFReaderIframe
                isModal={true}
                handleCancel={handleCancel}
                bookSlugId={router.query.bookId as string}
                isSampleReadingPag={true}
              />
            </div>
          </Modal>
        </div>
        <ShouldRender check={!screens.lg}>
          <div className='absolute  inset-0  backdrop-blur-2xl -z-10'></div>
          <div className='absolute inset-0   bg-[#00000099]  -z-20'></div>
          <div className='absolute inset-0 lg:hidden -top-[75%] w-full block  h-full -z-30 overflow-hidden'>
            <Image
              src={slidCourseContainerData?.bookImage as string}
              alt='Background Image'
              layout='fill'
              priority
              className='w-full h-full object-cover '
            />
          </div>
        </ShouldRender>
      </div>
    </ConfigProvider>
  );
};

export default SlidCourseContainer;
